import { Injectable } from '@angular/core';
import { ListDetailManagementService } from '@app/lists/shared/list-detail-management/services/list-detail-management.service';
import {
  ActionSheetController,
  ModalController,
  PopoverController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState } from '@panamax/app-state';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { List } from '@usf/list-types';
import { ListAnalyticsService, ListConstants } from '@usf/ngrx-list';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { ListsOverlayService } from '../../../lists/services/lists-overlay.service';
import {
  ItemTypes,
  ListDetailViewModel,
  ListManagementViewModel,
} from '../../../lists/shared/list-detail-management/model/list-detail-management-view.model';
import { ProductAlternativeSearchService } from '../../../product-detail/services/product-alternative-search.service';
import { ProductDetailTagsService } from '../../../product-detail/services/product-detail-tags.service';
import { QUICK_VIEW_SEGMENT } from '../../constants/lists-constants';
import { UsfProductCardModeEnum } from '../../constants/usf-product-card-mode.enum';
import { NavigationHelperService } from '../../helpers/navigation.helpers.service';
import { EllipsisClickData } from '../../models/usf-product-card.model';
import { ModalService } from '../modal/modal.service';
import { ProductCardService } from './product-card.service';

@Injectable({
  providedIn: 'root',
})
export class ListProductCardService extends ProductCardService {
  constructor(
    actionSheetController: ActionSheetController,
    modalController: ModalController,
    popoverController: PopoverController,
    panAppState: PanAppState,
    translateService: TranslateService,
    listsOverlayService: ListsOverlayService,
    modalService: ModalService,
    navigationHelperService: NavigationHelperService,
    productAlternativeSearchService: ProductAlternativeSearchService,
    productDetailTagsService: ProductDetailTagsService,
    private listAnalyticsService: ListAnalyticsService,
    listDetailManagementService: ListDetailManagementService,
    analyticsService: ProductAnalyticsService,
    toggleNewSearchService: ToggleNewSearchService,
  ) {
    super(
      actionSheetController,
      modalController,
      popoverController,
      panAppState,
      translateService,
      listsOverlayService,
      modalService,
      navigationHelperService,
      productAlternativeSearchService,
      productDetailTagsService,
      listDetailManagementService,
      analyticsService,
      toggleNewSearchService,
    );
  }

  async constructEllipsisButtons(
    ellipsisClickData: EllipsisClickData,
    viewModel?: ListDetailViewModel | ListManagementViewModel,
  ) {
    let buttons = [];
    let listType = this.getListTypeId(ellipsisClickData);

    if (ellipsisClickData.itemType === ItemTypes.product) {
      if (
        this.shouldAddAcceptReplacement(ellipsisClickData.product, listType)
      ) {
        buttons.push(this.getAcceptReplacementBtn(ellipsisClickData));
      }
      if (this.shouldAddSearchForSubs(ellipsisClickData.product, listType)) {
        buttons.push(
          this.getSearchForSubsBtn(
            ellipsisClickData,
            SEARCH_PAGES_ENUM.listDetail,
          ),
        );
      }
    }

    buttons.push(
      this.getQuickViewBtn(ellipsisClickData, SEARCH_PAGES_ENUM.listDetail),
    );
    if (this.shouldAddProductStatus(ellipsisClickData.product)) {
      buttons.push(this.getProductStatusBtn(ellipsisClickData));
    }

    this.getSellerQuickViewBtn(buttons, ellipsisClickData);

    if (
      this.shouldSeeNote(listType) &&
      ellipsisClickData.listItemNoteFeatureFlag
    ) {
      const noteExists =
        ellipsisClickData.product.item?.note !== undefined &&
        ellipsisClickData.product.item?.note !== '' &&
        ellipsisClickData.product.item?.note !== null;

      buttons.push(
        this.addCreateEditNoteButtonToEllipsis(
          noteExists,
          noteExists
            ? {
                ...ellipsisClickData,
                product: {
                  ...ellipsisClickData.product,
                  item: ellipsisClickData.product.item,
                },
              }
            : ellipsisClickData,
        ),
      );

      if (noteExists) {
        buttons.push(this.addDeleteNoteButtonToEllipsis(ellipsisClickData));
      }
    }

    if (
      ellipsisClickData.mode === UsfProductCardModeEnum.listDetail &&
      viewModel &&
      viewModel?.list?.listKey?.listTypeId === ListConstants.normalListTypeId
    ) {
      buttons.push(this.getDeleteProductBtn(ellipsisClickData, viewModel));
    }

    await this.getCompareBtn(buttons, ellipsisClickData);

    return buttons;
  }

  getAcceptReplacementBtn(ellipsisClickData: EllipsisClickData) {
    return {
      text: this.translateService.instant('i18n.lists.acceptReplacement'),
      handler: () => {
        this.dismissProductCardOptions(ellipsisClickData.isTouch);
        this.listsOverlayService.openReplaceProductModal(
          ellipsisClickData.product?.productNumber,
          ellipsisClickData.product?.alternative?.product?.productNumber,
          ellipsisClickData.list,
        );
      },
    };
  }

  quickViewAnalyticEvent(ellipsisClickData: EllipsisClickData) {
    let products: any[] = [];
    if (ellipsisClickData.product?.inventory?.isInStock) {
      products.push({
        attributes: ellipsisClickData.product.trackingAttributes,
        divisionApn: `${ellipsisClickData.product.summary.divisionNumber}-${ellipsisClickData.product.productNumber}`,
        primaryProduct: 1,
      });
    } else {
      let product: any = {
        attributes: ellipsisClickData.product.trackingAttributes,
        divisionApn: `${ellipsisClickData.product.summary.divisionNumber}-${ellipsisClickData.product.productNumber}`,
        primaryProduct: 1,
        exceptionView: '1',
      };
      if (ellipsisClickData.product?.hasAlternative)
        product = { ...product, exceptionNumberOfSubs: 1 };
      products.push(product);
    }

    if (
      ellipsisClickData.product?.hasAlternative &&
      ellipsisClickData.product?.alternative?.product
    ) {
      products.push({
        attributes:
          ellipsisClickData.product.alternative.product.trackingAttributes,
        divisionApn: `${ellipsisClickData.product.alternative.product.summary.divisionNumber}-${ellipsisClickData.product.alternative.product.productNumber}`,
        substituteView: '1',
        substitutionParentApn: `${ellipsisClickData.product.summary.divisionNumber}-${ellipsisClickData.product.productNumber}`,
      });
    }

    this.listAnalyticsService.trackQuickViewOpen(products);
  }

  getQuickViewModal(
    ellipsisClickData: EllipsisClickData,
    selectedSegment: string,
    originSearchPage?: string,
  ) {
    if (selectedSegment === QUICK_VIEW_SEGMENT)
      this.quickViewAnalyticEvent(ellipsisClickData);
    return super.getQuickViewModal(
      ellipsisClickData,
      selectedSegment,
      originSearchPage,
    );
  }

  directlyOpenCreateEditNote(
    isEdit: boolean,
    ellipsisClickData: EllipsisClickData,
  ) {
    this.listsOverlayService.openCreateEditNoteModal(
      isEdit,
      ellipsisClickData.product.item,
      ellipsisClickData.list as List,
    );
  }

  addCreateEditNoteButtonToEllipsis(
    isEdit: boolean,
    ellipsisClickData: EllipsisClickData,
  ) {
    return {
      text: isEdit
        ? this.translateService.instant('i18n.lists.note.editEntry')
        : this.translateService.instant('i18n.lists.note.createEntry'),
      handler: () => {
        this.dismissProductCardOptions(ellipsisClickData.isTouch);
        this.listsOverlayService.openCreateEditNoteModal(
          isEdit,
          ellipsisClickData.product.item,
          ellipsisClickData.list as List,
        );
      },
    };
  }

  // Avoiding binding mmanagement service or store to list-product-card-service
  addDeleteNoteButtonToEllipsis(ellipsisClickData: EllipsisClickData) {
    return {
      text: this.translateService.instant('i18n.lists.note.deleteEntry'),
      handler: () => {
        this.dismissProductCardOptions(ellipsisClickData.isTouch);
        this.listsOverlayService.requestThroughDeleteNote(
          ellipsisClickData.product.item,
          ellipsisClickData.list as List,
        );
      },
    };
  }
}
